/* src/components/PageSection.js */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import styles from "./PageSection.module.css"; // CSS Module

function PageSection({ title, children, cta, faqs, dark }) {
  const [hasError, setHasError] = useState(false);

  // Reset error state when component mounts
  useEffect(() => {
    setHasError(false);
  }, []);

  // Error boundary within the component
  if (hasError) {
    return (
      <section className={styles.pageSection}>
        <h2>Something went wrong</h2>
        <p>I'm sorry, but there was an error loading this page. Please try again later.</p>
        <Link to="/" className={styles.ctaButton}>Return to Home</Link>
      </section>
    );
  }

  // Wrap the content in an error handler
  try {
    return (
      <section
        className={`${styles.pageSection} ${
          dark ? styles.darkBackground : styles.lightBackground
        }`}
      >
        <h2>{title}</h2>
        {children}
        {cta && <Link to="/contact-us" className={styles.ctaButton}>Contact</Link>}
        {/* Render FAQs if provided */}
        {faqs && faqs.length > 0 && (
          <div className={styles.faqSection}>
            <h3>Frequently Asked Questions</h3>
            <ul>
              {faqs.map((faq, index) => (
                <li key={index} className={styles.faqItem}>
                  <strong>{faq.question}</strong>
                  <p>{faq.answer}</p>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>
    );
  } catch (error) {
    console.error("Error in PageSection:", error);
    setHasError(true);
    return (
      <section className={styles.pageSection}>
        <h2>Something went wrong</h2>
        <p>I'm sorry, but there was an error loading this page. Please try again later.</p>
        <Link to="/" className={styles.ctaButton}>Return to Home</Link>
      </section>
    );
  }
}

PageSection.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  cta: PropTypes.bool,
  faqs: PropTypes.arrayOf(
    PropTypes.shape({
      question: PropTypes.string,
      answer: PropTypes.string,
    })
  ),
  dark: PropTypes.bool, // New prop to determine background
};

PageSection.defaultProps = {
  cta: false,
  faqs: [],
  dark: false, // Default to light background
};

export default PageSection;
